import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Sparkles } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { ExerciseSet, Workout } from "@/components/workouts/workoutsColumns";
import { PeriodisationExerciseSetCard } from "@/components/training/PeriodisationExerciseSetCard";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/ui";

export type PeriodisationScheduleItem = {
  id: string;
  workout_id: string;
  scheduled_date: string;
  order_index: number;
};

export type AutoPeriodiseExerciseUpdate = {
  workoutId: string;
  exerciseId: string;
  occurrence: number;
  sets: ExerciseSet[];
};

type PeriodisationMatrixProps = {
  selectedItems: PeriodisationScheduleItem[];
  workoutById: Map<string, Workout>;
  onChangeExercise: (
    workoutId: string,
    exerciseId: string,
    updates: {
      sets?: ExerciseSet[];
    },
    occurrence?: number,
  ) => void;
  onAutoPeriodiseApply?: (updates: AutoPeriodiseExerciseUpdate[]) => void;
  onAutoSweepStateChange?: (active: boolean) => void;
  onAutoPeriodiseGenerate?: () => Promise<AutoPeriodiseExerciseUpdate[]>;
  showAutoPeriodise?: boolean;
};

type ExerciseRow = {
  key: string;
  exerciseId: string;
  occurrence: number;
  name: string;
  image?: string | null;
};

type ProgressionType = "compound" | "isolation";
type DeloadMode = "reduce_reps" | "reduce_sets";
type Aggressiveness = "conservative" | "standard" | "aggressive";
type TrackingType =
  Workout["exerciseCollection"][number][number]["tracking_type"];

type AutoPeriodiseSettings = {
  deloadWeekIndex?: number;
  deloadMode?: DeloadMode;
  aggressiveness?: Aggressiveness;
  maxAddedSetsPerWorkout?: number;
  maxSetsCompound?: number;
  maxSetsIsolation?: number;
  enableWeightProgression?: boolean;
  weightIncreasePctCompound?: number;
  weightIncreasePctIsolation?: number;
  weightRounding?: number;
};

const DEFAULT_REP_CAP = 15;
const DEFAULT_START_REPS = 8;
const DEFAULT_END_REPS = 10;
const MIN_REPS = 3;
const EARLY_SET_REP_THRESHOLD = 12;
const AUTO_GENERATING_DURATION_MS = 5000;
const AUTO_SWEEP_DURATION_MS = 4200;
const ROW_HIGHLIGHT_CLASS =
  "bg-primary/10 shadow-[inset_0_1px_0_0_hsl(var(--primary)/0.4),inset_0_-1px_0_0_hsl(var(--primary)/0.4)] dark:bg-primary/18";

const inferProgressionType = (exerciseName: string): ProgressionType => {
  const name = exerciseName.toLowerCase();
  if (
    /(squat|deadlift|press|row|pull-?up|chin-?up|bench|lunge|hip thrust)/.test(
      name,
    )
  ) {
    return "compound";
  }
  return "isolation";
};

const ensureRepRange = (set: ExerciseSet) => {
  const start = set.startRange ?? set.endRange ?? DEFAULT_START_REPS;
  const end = set.endRange ?? Math.max(start, DEFAULT_END_REPS);
  return {
    start: Math.max(MIN_REPS, start),
    end: Math.max(MIN_REPS, Math.max(end, start)),
  };
};

const canIncreaseReps = (sets: ExerciseSet[], repCap = DEFAULT_REP_CAP) =>
  sets.length > 0 && sets.some((set) => ensureRepRange(set).end < repCap);

const increaseReps = (sets: ExerciseSet[], repCap = DEFAULT_REP_CAP) =>
  sets.map((set) => {
    const range = ensureRepRange(set);
    if (range.end >= repCap) {
      return {
        ...set,
        startRange: range.start,
        endRange: range.end,
      };
    }
    return {
      ...set,
      startRange: Math.min(repCap, range.start + 1),
      endRange: Math.min(repCap, range.end + 1),
    };
  });

const deloadReps = (sets: ExerciseSet[]) =>
  sets.map((set) => {
    const range = ensureRepRange(set);
    const nextStart = Math.max(MIN_REPS, range.start - 2);
    const nextEnd = Math.max(nextStart, range.end - 2);
    return {
      ...set,
      startRange: nextStart,
      endRange: nextEnd,
    };
  });

const deloadSets = (sets: ExerciseSet[]) =>
  sets.length > 1 ? sets.slice(0, sets.length - 1) : sets;

const maxSetsForType = (
  type: ProgressionType,
  settings: Required<AutoPeriodiseSettings>,
) =>
  type === "compound" ? settings.maxSetsCompound : settings.maxSetsIsolation;

const shouldAllowSetIncrease = (
  type: ProgressionType,
  aggressiveness: Aggressiveness,
) => {
  if (aggressiveness === "aggressive") return true;
  if (aggressiveness === "conservative") return type === "compound";
  return true;
};

const addSet = (sets: ExerciseSet[]) => {
  if (sets.length === 0) {
    return [
      {
        id: uuidv4(),
        startRange: DEFAULT_START_REPS,
        endRange: DEFAULT_END_REPS,
      },
    ];
  }
  const tail = sets[sets.length - 1];
  return [...sets, { ...tail, id: uuidv4() }];
};

const roundToIncrement = (value: number, increment: number) =>
  Math.round(value / increment) * increment;

const bumpWeightAndDropReps = (
  sets: ExerciseSet[],
  pct: number,
  rounding: number,
  dropReps = 1,
) =>
  sets.map((set) => {
    const range = ensureRepRange(set);
    const nextStart = Math.max(MIN_REPS, range.start - dropReps);
    const nextEnd = Math.max(nextStart, range.end - dropReps);
    const nextWeight =
      set.targetWeight === undefined || set.targetWeight === null
        ? undefined
        : roundToIncrement(set.targetWeight * (1 + pct / 100), rounding);
    return {
      ...set,
      startRange: nextStart,
      endRange: nextEnd,
      targetWeight: nextWeight,
    };
  });

export const PeriodisationMatrix = ({
  selectedItems,
  workoutById,
  onChangeExercise,
  onAutoPeriodiseApply,
  onAutoSweepStateChange,
  onAutoPeriodiseGenerate,
  showAutoPeriodise = true,
}: PeriodisationMatrixProps) => {
  const [hoveredRowKey, setHoveredRowKey] = useState<string | null>(null);
  const [autoPhase, setAutoPhase] = useState<"idle" | "generating" | "sweeping">(
    "idle",
  );
  const matrixRef = useRef<HTMLDivElement | null>(null);
  const [viewportMetrics, setViewportMetrics] = useState({
    scrollLeft: 0,
    viewportWidth: 0,
  });
  const exerciseColumnWidth = 200;
  const workoutColumnWidth = 360;
  const matrixWidth =
    exerciseColumnWidth + selectedItems.length * workoutColumnWidth;
  const columnOverscan = 2;
  const exerciseRows = useMemo<ExerciseRow[]>(() => {
    const byExercise = new Map<
      string,
      { name: string; image?: string | null; maxOccurrences: number }
    >();
    const rows: ExerciseRow[] = [];

    selectedItems.forEach((item) => {
      const workout = workoutById.get(item.workout_id);
      if (!workout) return;

      const occurrencesInWorkout = new Map<string, number>();
      workout.exerciseCollection.flat().forEach((exercise) => {
        const count = (occurrencesInWorkout.get(exercise.id) ?? 0) + 1;
        occurrencesInWorkout.set(exercise.id, count);

        const existing = byExercise.get(exercise.id);
        if (!existing) {
          byExercise.set(exercise.id, {
            name: exercise.name,
            image: (exercise as { url?: string | null }).url ?? null,
            maxOccurrences: count,
          });
          return;
        }
        if (count > existing.maxOccurrences) {
          existing.maxOccurrences = count;
        }
      });
    });

    byExercise.forEach((entry, exerciseId) => {
      for (
        let occurrence = 0;
        occurrence < entry.maxOccurrences;
        occurrence += 1
      ) {
        rows.push({
          key: `${exerciseId}:${occurrence}`,
          exerciseId,
          occurrence,
          name:
            entry.maxOccurrences > 1
              ? `${entry.name} (${occurrence + 1})`
              : entry.name,
          image: entry.image,
        });
      }
    });

    return rows;
  }, [selectedItems, workoutById]);

  useEffect(() => {
    const matrixNode = matrixRef.current;
    if (!matrixNode) return;
    const viewport = matrixNode.closest(
      "[data-radix-scroll-area-viewport]",
    ) as HTMLElement | null;
    if (!viewport) return;

    let rafId: number | null = null;
    const sync = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        setViewportMetrics({
          scrollLeft: viewport.scrollLeft,
          viewportWidth: viewport.clientWidth,
        });
      });
    };

    sync();
    viewport.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);

    return () => {
      viewport.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  const { visibleItems, leftSpacerWidth, rightSpacerWidth, startIndex } =
    useMemo(() => {
      const totalColumns = selectedItems.length;
      if (totalColumns === 0) {
        return {
          visibleItems: [] as PeriodisationScheduleItem[],
          leftSpacerWidth: 0,
          rightSpacerWidth: 0,
          startIndex: 0,
        };
      }

      const visibleWidth = Math.max(
        0,
        viewportMetrics.viewportWidth - exerciseColumnWidth,
      );

      // scrollLeft includes sticky column area, so offset by exercise column width.
      const startPx = Math.max(
        0,
        viewportMetrics.scrollLeft - exerciseColumnWidth,
      );
      const endPx = startPx + visibleWidth;
      let nextStart = Math.floor(startPx / workoutColumnWidth) - columnOverscan;
      let nextEnd = Math.ceil(endPx / workoutColumnWidth) + columnOverscan - 1;

      if (visibleWidth <= 0) {
        nextStart = 0;
        nextEnd = totalColumns - 1;
      }

      const clampedStart = Math.max(0, Math.min(totalColumns - 1, nextStart));
      const clampedEnd = Math.max(
        clampedStart,
        Math.min(totalColumns - 1, nextEnd),
      );
      const nextVisibleItems = selectedItems.slice(
        clampedStart,
        clampedEnd + 1,
      );

      return {
        visibleItems: nextVisibleItems,
        leftSpacerWidth: clampedStart * workoutColumnWidth,
        rightSpacerWidth: (totalColumns - clampedEnd - 1) * workoutColumnWidth,
        startIndex: clampedStart,
      };
    }, [
      columnOverscan,
      exerciseColumnWidth,
      selectedItems,
      viewportMetrics.scrollLeft,
      viewportMetrics.viewportWidth,
      workoutColumnWidth,
    ]);

  const gridTemplateColumns = useMemo(() => {
    const columns = [`${exerciseColumnWidth}px`];
    if (leftSpacerWidth > 0) columns.push(`${leftSpacerWidth}px`);
    if (visibleItems.length > 0) {
      columns.push(`repeat(${visibleItems.length}, ${workoutColumnWidth}px)`);
    }
    if (rightSpacerWidth > 0) columns.push(`${rightSpacerWidth}px`);
    return columns.join(" ");
  }, [
    exerciseColumnWidth,
    leftSpacerWidth,
    rightSpacerWidth,
    visibleItems.length,
    workoutColumnWidth,
  ]);

  const computeAutoUpdates = (): AutoPeriodiseExerciseUpdate[] => {
    const settings: Required<AutoPeriodiseSettings> = {
      deloadWeekIndex: selectedItems.length - 1,
      deloadMode: "reduce_reps",
      aggressiveness: "standard",
      maxAddedSetsPerWorkout: 2,
      maxSetsCompound: 5,
      maxSetsIsolation: 4,
      enableWeightProgression: false,
      weightIncreasePctCompound: 2.5,
      weightIncreasePctIsolation: 5,
      weightRounding: 2.5,
    };

    const addedSetsByWeek = new Array(selectedItems.length).fill(0);
    const updates: AutoPeriodiseExerciseUpdate[] = [];

    exerciseRows.forEach((row) => {
      const occurrences = selectedItems
        .map((item, weekIndex) => {
          const workout = workoutById.get(item.workout_id);
          if (!workout) return null;
          const exercise =
            workout.exerciseCollection
              .flat()
              .filter((entry) => entry.id === row.exerciseId)[row.occurrence] ??
            null;
          if (!exercise) return null;
          return {
            weekIndex,
            workoutId: workout.id,
            exerciseId: exercise.id,
            exerciseName: exercise.name,
            trackingType: exercise.tracking_type as TrackingType,
            sets: exercise.sets ?? [],
          };
        })
        .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

      occurrences.forEach((occ) => {
        const isTimedOrDistance =
          occ.trackingType === "timed" ||
          occ.trackingType === "distance_short" ||
          occ.trackingType === "distance_long";
        if (isTimedOrDistance) return;

        const progressionType = inferProgressionType(occ.exerciseName);
        const isDeloadWeek = occ.weekIndex === settings.deloadWeekIndex;
        let nextSets = occ.sets;
        let didChange = false;

        if (isDeloadWeek) {
          nextSets =
            settings.deloadMode === "reduce_sets"
              ? deloadSets(nextSets)
              : deloadReps(nextSets);
          didChange = true;
        } else if (occ.weekIndex !== 0) {
          const canAddSet =
            shouldAllowSetIncrease(
              progressionType,
              settings.aggressiveness,
            ) &&
            addedSetsByWeek[occ.weekIndex] < settings.maxAddedSetsPerWorkout &&
            nextSets.length < maxSetsForType(progressionType, settings);
          const shouldAddSetEarly =
            canAddSet &&
            settings.aggressiveness !== "conservative" &&
            occ.weekIndex >= 2 &&
            nextSets.length === 1 &&
            nextSets.every(
              (set) => ensureRepRange(set).end >= EARLY_SET_REP_THRESHOLD,
            );

          if (shouldAddSetEarly) {
            nextSets = addSet(nextSets);
            addedSetsByWeek[occ.weekIndex] += 1;
            didChange = true;
          } else if (canIncreaseReps(nextSets)) {
            nextSets = increaseReps(nextSets);
            didChange = true;
          } else {
            if (settings.enableWeightProgression) {
              const pct =
                progressionType === "isolation"
                  ? settings.weightIncreasePctIsolation
                  : settings.weightIncreasePctCompound;
              const hasAnyWeight = nextSets.some(
                (set) =>
                  set.targetWeight !== undefined && set.targetWeight !== null,
              );

              if (hasAnyWeight) {
                nextSets = bumpWeightAndDropReps(
                  nextSets,
                  pct,
                  settings.weightRounding,
                  1,
                );
                didChange = true;
              } else if (canAddSet) {
                nextSets = addSet(nextSets);
                addedSetsByWeek[occ.weekIndex] += 1;
                didChange = true;
              }
            } else if (canAddSet) {
              nextSets = addSet(nextSets);
              addedSetsByWeek[occ.weekIndex] += 1;
              didChange = true;
            }
          }
        }

        if (!didChange) return;
        updates.push({
          workoutId: occ.workoutId,
          exerciseId: occ.exerciseId,
          occurrence: row.occurrence,
          sets: nextSets,
        });
      });
    });

    return updates;
  };

  const applyAutoUpdates = (updates: AutoPeriodiseExerciseUpdate[]) => {
    if (updates.length === 0) return;
    if (onAutoPeriodiseApply) {
      onAutoPeriodiseApply(updates);
      return;
    }
    updates.forEach((update) => {
      onChangeExercise(update.workoutId, update.exerciseId, { sets: update.sets }, update.occurrence);
    });
  };

  const handleAutoPeriodise = async () => {
    if (
      !showAutoPeriodise ||
      selectedItems.length < 2 ||
      exerciseRows.length === 0 ||
      autoPhase !== "idle"
    ) {
      return;
    }

    setAutoPhase("generating");
    try {
      const updatesPromise = onAutoPeriodiseGenerate
        ? onAutoPeriodiseGenerate()
        : Promise.resolve(computeAutoUpdates());
      const delayPromise = new Promise<void>((resolve) => {
        window.setTimeout(resolve, AUTO_GENERATING_DURATION_MS);
      });

      const [, updates] = await Promise.all([delayPromise, updatesPromise]);
      if (!updates || updates.length === 0) {
        setAutoPhase("idle");
        onAutoSweepStateChange?.(false);
        return;
      }

      setAutoPhase("sweeping");
      window.requestAnimationFrame(() => {
        onAutoSweepStateChange?.(true);
        applyAutoUpdates(updates);
      });
      window.setTimeout(() => {
        setAutoPhase("idle");
        onAutoSweepStateChange?.(false);
      }, AUTO_SWEEP_DURATION_MS + 180);
    } catch {
      setAutoPhase("idle");
      onAutoSweepStateChange?.(false);
    }
  };

  return (
    <div className="relative inline-block">
      <div
        ref={matrixRef}
        className="inline-grid min-w-[1200px]"
        style={{
          width: `${matrixWidth}px`,
          minWidth: `${matrixWidth}px`,
          gridTemplateColumns,
        }}
      >
      <div className="sticky left-0 top-0 z-30 flex items-center justify-between gap-2 border-b border-r bg-background px-4 py-3">
        <span className="font-semibold">Exercise</span>
        {showAutoPeriodise ? (
          <Button
            type="button"
            size="sm"
            onClick={handleAutoPeriodise}
            disabled={
              selectedItems.length < 2 || exerciseRows.length === 0 || autoPhase !== "idle"
            }
            className="h-8 border border-violet-400/50 bg-gradient-to-r from-violet-600 to-fuchsia-500 px-3 text-xs font-semibold text-white shadow-[0_0_0_1px_rgba(139,92,246,0.25),0_8px_24px_rgba(147,51,234,0.35)] transition hover:from-violet-500 hover:to-fuchsia-400 focus-visible:ring-violet-400"
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            {autoPhase === "idle" ? "Auto" : "Generating..."}
          </Button>
        ) : null}
      </div>
      {leftSpacerWidth > 0 ? (
        <div
          className="sticky top-0 z-20 border-b border-r bg-background"
          aria-hidden
        />
      ) : null}
      {visibleItems.map((item, visibleIdx) => {
        const workout = workoutById.get(item.workout_id);
        return (
          <div
            key={`header-${item.id}`}
            className="sticky top-0 z-20 flex items-center justify-between gap-2 border-b border-r bg-background px-4 py-3"
            data-column-index={startIndex + visibleIdx}
          >
            <span className="truncate font-semibold">
              {workout?.name ?? "Workout"}
            </span>
            <span className="rounded border bg-background px-2 py-0.5 text-xs text-muted-foreground">
              {format(new Date(item.scheduled_date), "MMM d")}
            </span>
          </div>
        );
      })}
      {rightSpacerWidth > 0 ? (
        <div
          className="sticky top-0 z-20 border-b border-r bg-background"
          aria-hidden
        />
      ) : null}

      {exerciseRows.map((row) => (
        <Fragment key={`row-${row.key}`}>
          <div
            className={cn(
              "sticky left-0 z-10 flex min-h-[120px] items-center gap-3 border-b border-r bg-background px-4 py-3 transition-colors",
              hoveredRowKey === row.key && ROW_HIGHLIGHT_CLASS,
            )}
            onMouseEnter={() => setHoveredRowKey(row.key)}
            onMouseLeave={() =>
              setHoveredRowKey((current) =>
                current === row.key ? null : current,
              )
            }
          >
            {row.image ? (
              <img
                src={row.image}
                alt={row.name}
                className="h-9 w-9 rounded object-cover"
              />
            ) : null}
            <span className="line-clamp-2 font-medium">{row.name}</span>
          </div>
          {leftSpacerWidth > 0 ? (
            <div
              className={cn(
                "min-h-[120px] border-b border-r transition-colors",
                hoveredRowKey === row.key && ROW_HIGHLIGHT_CLASS,
              )}
              aria-hidden
            />
          ) : null}

          {visibleItems.map((item) => {
            const workout = workoutById.get(item.workout_id);
            const exercise = workout
              ? workout.exerciseCollection
                  .flat()
                  .filter((entry) => entry.id === row.exerciseId)[
                  row.occurrence
                ]
              : undefined;

            if (!workout || !exercise) {
              return (
                <div
                  key={`cell-${row.key}-${item.id}`}
                  className={cn(
                    "flex min-h-[120px] items-center justify-center border-b border-r px-4 py-3 text-sm italic text-muted-foreground transition-colors",
                    hoveredRowKey === row.key && ROW_HIGHLIGHT_CLASS,
                  )}
                  onMouseEnter={() => setHoveredRowKey(row.key)}
                  onMouseLeave={() =>
                    setHoveredRowKey((current) =>
                      current === row.key ? null : current,
                    )
                  }
                >
                  Not in this workout
                </div>
              );
            }

            return (
              <div
                key={`cell-${row.key}-${item.id}`}
                className={cn(
                  "group/period-cell flex min-h-[120px] items-start border-b border-r px-4 py-3 transition-colors",
                  hoveredRowKey === row.key && ROW_HIGHLIGHT_CLASS,
                )}
                onMouseEnter={() => setHoveredRowKey(row.key)}
                onMouseLeave={() =>
                  setHoveredRowKey((current) =>
                    current === row.key ? null : current,
                  )
                }
              >
                <PeriodisationExerciseSetCard
                  exercise={exercise}
                  onSetsChange={(sets) =>
                    onChangeExercise(
                      workout.id,
                      exercise.id,
                      { sets },
                      row.occurrence,
                    )
                  }
                />
              </div>
            );
          })}
          {rightSpacerWidth > 0 ? (
            <div
              className={cn(
                "min-h-[120px] border-b border-r transition-colors",
                hoveredRowKey === row.key && ROW_HIGHLIGHT_CLASS,
              )}
              aria-hidden
            />
          ) : null}
        </Fragment>
      ))}
      </div>

      {autoPhase === "generating" ? (
        <div className="pointer-events-none fixed inset-0 z-[70] flex items-center justify-center bg-background/35 backdrop-blur-[2px]">
          <div className="rounded-xl border border-violet-300/60 bg-background/95 px-6 py-4 shadow-[0_8px_36px_rgba(139,92,246,0.35)]">
            <div className="flex items-center gap-3 text-sm font-semibold text-violet-700">
              <Sparkles className="h-4 w-4 animate-pulse" />
              <span className="animate-pulse">Generating Progression...</span>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );
};
